/**
 * Offer folder rail — collapsible sidebar, inline create, rename, delete, move.
 * Call init() from offer-index.ts after DOMContentLoaded.
 */

import { postJson } from '../lib/api-client';

const COLLAPSED_KEY = 'offer-folder-rail-collapsed';

export function init(): void {
    const container = document.getElementById('offer-index-container') as HTMLElement;

    const urls = {
        createFolder: container?.dataset.urlCreateFolder ?? '',
        renameFolder: container?.dataset.urlRenameFolder ?? '',
        deleteFolder: container?.dataset.urlDeleteFolder ?? '',
        moveFolder:   container?.dataset.urlMoveFolder   ?? '',
    };

    const moveAgencyId = parseInt(container?.dataset.agencyId ?? '0', 10);

    // Move-to-folder is wired unconditionally — works even when the folder rail is not in the DOM
    document.addEventListener('click', async (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.btn-move-to-folder');
        if (!btn) return;

        e.preventDefault();
        const offerId     = parseInt(btn.dataset.offerId ?? '0', 10);
        const rawFolderId = btn.dataset.folderId;
        const folderId    = rawFolderId ? parseInt(rawFolderId, 10) : null;

        try {
            await postJson(urls.moveFolder, { offerId, folderId, agencyId: moveAgencyId });
            window.location.reload();
        } catch {
            console.error('Failed to move offer to folder');
        }
    });

    const rail = document.getElementById('folder-rail') as HTMLElement | null;
    if (!rail) return;

    const activeGroup = container?.dataset.activeGroup ?? 'Accommodation';
    const agencyId    = parseInt(container?.dataset.agencyId    ?? '0', 10);

    // Restore collapsed state
    if (localStorage.getItem(COLLAPSED_KEY) === '1') {
        rail.classList.add('collapsed');
    }

    // Toggle collapse
    document.getElementById('folder-rail-toggle')?.addEventListener('click', () => {
        rail.classList.toggle('collapsed');
        localStorage.setItem(COLLAPSED_KEY, rail.classList.contains('collapsed') ? '1' : '0');
    });

    // New folder button
    const newBtn       = document.getElementById('folder-new-btn');
    const newInputRow  = document.getElementById('folder-new-input-row');
    const newInput     = document.getElementById('folder-new-input') as HTMLInputElement | null;

    newBtn?.addEventListener('click', () => {
        rail.classList.remove('collapsed');
        newInputRow?.classList.remove('d-none');
        newBtn.classList.add('d-none');
        newInput?.focus();
    });

    function cancelNewFolder() {
        newInputRow?.classList.add('d-none');
        newBtn?.classList.remove('d-none');
        if (newInput) newInput.value = '';
    }

    async function saveNewFolder() {
        const name = newInput?.value.trim() ?? '';
        if (!name) { cancelNewFolder(); return; }

        try {
            const res = await postJson<{ id: number; name: string }>(urls.createFolder, { agencyId, group: activeGroup, name });
            if (res.success && res.data) {
                window.location.href = addFolderIdToUrl(res.data.id);
            } else {
                cancelNewFolder();
            }
        } catch {
            cancelNewFolder();
        }
    }

    newInput?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter')  { e.preventDefault(); saveNewFolder(); }
        if (e.key === 'Escape') cancelNewFolder();
    });
    newInput?.addEventListener('blur', () => saveNewFolder());

    // Rename buttons — SweetAlert input prompt
    rail.querySelectorAll<HTMLButtonElement>('.btn-folder-rename').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const folderId   = parseInt(btn.dataset.folderId ?? '0', 10);
            const current    = btn.dataset.folderName ?? '';

            const Swal = (window as any).Swal;
            let name: string | null = null;

            if (Swal) {
                const result = await Swal.fire({
                    title: container.dataset.locRenameTitle ?? 'Rename folder',
                    input: 'text',
                    inputValue: current,
                    showCancelButton: true,
                    confirmButtonText: container.dataset.locSave ?? 'Save',
                    cancelButtonText:  container.dataset.locCancel ?? 'Cancel',
                    inputValidator: (v: string) => v.trim() ? undefined : (container.dataset.locNameRequired ?? 'Name is required'),
                });
                if (!result.isConfirmed) return;
                name = result.value?.trim() ?? null;
            } else {
                name = prompt(container.dataset.locRenameTitle ?? 'Rename folder', current)?.trim() ?? null;
            }

            if (!name || name === current) return;

            await postJson(urls.renameFolder, { id: folderId, name });
            window.location.reload();
        });
    });

    // Delete buttons
    rail.querySelectorAll<HTMLButtonElement>('.btn-folder-delete').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const folderId   = parseInt(btn.dataset.folderId ?? '0', 10);
            const folderName = btn.dataset.folderName ?? '';

            const confirmed = await confirmDelete(folderName, container);
            if (!confirmed) return;

            await postJson(urls.deleteFolder, { id: folderId });
            window.location.href = removeFolderIdFromUrl();
        });
    });

}

function addFolderIdToUrl(folderId: number): string {
    const url = new URL(window.location.href);
    url.searchParams.set('folderId', String(folderId));
    return url.toString();
}

function removeFolderIdFromUrl(): string {
    const url = new URL(window.location.href);
    url.searchParams.delete('folderId');
    return url.toString();
}

async function confirmDelete(folderName: string, container: HTMLElement): Promise<boolean> {
    const Swal = (window as any).Swal;
    if (Swal) {
        const result = await Swal.fire({
            title: container.dataset.locDeleteTitle ?? 'Delete folder',
            text:  (container.dataset.locDeleteText ?? 'Delete "{0}"? Offers in this folder will become uncategorized.').replace('{0}', folderName),
            icon:  'warning',
            showCancelButton:    true,
            confirmButtonColor:  'var(--volt-danger, #e04f5f)',
            confirmButtonText:   container.dataset.locDelete ?? 'Delete',
            cancelButtonText:    container.dataset.locCancel ?? 'Cancel',
        });
        return result.isConfirmed;
    }
    return confirm(`Delete folder "${folderName}"?`);
}
